  import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
  import {
    doc,
    setDoc,
    collection,
    query,
    where,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    increment,
    deleteField,
    onSnapshot
  } from 'firebase/firestore';
  import { db } from '../../firebase/firebase';

  const mergeJobById = (list, update) => {
    if (!Array.isArray(list)) return list;

    return list.map((job) => (job.id === update.id ? { ...job, ...update } : job));
  };

  /* makes reference to get a auto-generated id, inserts the doc 
  to Firestore using the new id, returns the added object */
  export const createJobForm = createAsyncThunk('jobs/createJobForm', async (payload) => {
    const formRef = doc(collection(db, 'jobs'));
    await setDoc(formRef, { ...payload, id: formRef.id });
    return { ...payload, id: formRef.id };
  });

  export const updateJobForm = createAsyncThunk('jobs/updateJobForm', async ({ id, data }) => {
    const jobRef = doc(db, 'jobs', id);
    await updateDoc(jobRef, { ...data });
    const updatedJob = await getDoc(jobRef);
    return { ...updatedJob.data(), id };
  });

  export const updateScheduleSettings = createAsyncThunk(
    'jobs/updateScheduleSettings',
    async ({ id, scheduleSettings }) => {
      const jobRef = doc(db, 'jobs', id);
      // Update only the scheduleSettings field
      await updateDoc(jobRef, { scheduleSettings });

      return { id, scheduleSettings }; // Return the updated scheduleSettings for further use
    }
  );

  export const fetchAllJobs = createAsyncThunk(
    'jobs/fetchAllJobs',
    async (forceRefresh = false, { getState, rejectWithValue }) => {
      const { lastFetched, cache } = getState().jobs;
      if (!forceRefresh && lastFetched && new Date().getTime() - lastFetched < 10000) {
        return cache;
      }

      try {
        const documents = [];
        const snap = await getDocs(collection(db, 'jobs'));

        // Check if Firestore returned any documents
        if (snap.empty) {
          console.log('No jobs found in Firestore.');
          return documents; // return an empty array if no jobs are found
        }

        // Process each document fetched from Firestore
        snap.forEach((doc) => {
          const jobData = doc.data();

          // Add fallback values for missing fields in Firestore documents
          const job = {
            ...jobData,
            id: jobData.id || doc.id,
            price: jobData.price ?? 0, // Fallback if price is missing
            category: jobData.category || 'Unknown category', // Fallback if category is missing
            status: jobData.status || 'active', // Fallback if status is missing
            userVatNumber: jobData.userVatNumber || 'N/A' // Handle other fields as necessary
          };

          documents.push(job);
        });

        return documents;
      } catch (error) {
        console.error('Error fetching jobs from Firestore:', error.message);
        return rejectWithValue(error.message);
      }
    }
  );

  export const fetchActiveJobs = createAsyncThunk(
    'jobs/fetchActiveJobs',
    async (_, { rejectWithValue }) => {
      try {
        const activeJobs = [];
        const q = query(collection(db, 'jobs'), where('status', '==', 'active'));
        const snap = await getDocs(q);

        snap.forEach((doc) => {
          const jobData = doc.data();
          const job = {
            ...jobData,
            price: jobData.price ?? 0,
            category: jobData.category || 'Unknown category',
            status: jobData.status || 'inactive',
            userVatNumber: jobData.userVatNumber || 'N/A'
          };
          activeJobs.push(job);
        });

        return activeJobs;
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const fetchJobsByQuery = createAsyncThunk(
    'jobs/fetchJobsByQuery',
    async ({ key, value }) => {
      try {
        const documents = [];
        const jobsRef = collection(db, 'jobs');
        const q = query(jobsRef, where(key, '==', value));
        const snap = await getDocs(q);
        snap.forEach((doc) => {
          documents.push({ ...doc.data() });
        });

        return documents;
      } catch (error) {
        return error;
      }
    }
  );

  // In your Redux slice file
  export const removeJobFields = createAsyncThunk(
    'jobs/removeJobFields',
    async ({ jobId, fieldsToRemove }, { rejectWithValue }) => {
      try {
        const jobRef = doc(db, 'jobs', jobId);
        const updatedData = {};
        fieldsToRemove.forEach((field) => {
          updatedData[field] = deleteField(); // Using Firestore's deleteField to remove fields
        });
        await updateDoc(jobRef, updatedData);
        return { jobId, fieldsToRemove };
      } catch (error) {
        return rejectWithValue(error.message); // Return the error message
      }
    }
  );

  export const fetchCategoryJobs = createAsyncThunk(
    'jobs/fetchCategoryJobs',
    async ({ key, value }) => {
      try {
        const documents = [];
        const jobsRef = collection(db, 'jobs');
        const q = query(jobsRef, where(key, '==', value));
        const snap = await getDocs(q);
        snap.forEach((doc) => {
          documents.push({ ...doc.data() });
        });
        return documents;
      } catch (error) {
        return error;
      }
    }
  );

  export const deleteJobById = createAsyncThunk('jobs/deleteJobById', async (jobId) => {
    try {
      await deleteDoc(doc(db, 'jobs', jobId));
      return jobId;
    } catch (error) {
      return error;
    }
  });

  export const toggleJobStatus = createAsyncThunk('jobs/toggleJobStatus', async ({ id, status }) => {
    //console.log("jobSlice: ", id + " current: " + status);
    try {
      await updateDoc(doc(db, 'jobs', id), {
        status: status
      });
      return { id, status };
    } catch (error) {
      return error;
    }
  });

  export const addPageview = createAsyncThunk('jobs/addPageview', async (jobId) => {
    try {
      await updateDoc(doc(db, 'jobs', jobId), {
        pageviews: increment(1)
      });
    } catch (error) {
      return error;
    }
  });

  // export const fetchJobsByUserId = createAsyncThunk(
  //   'jobs/fetchUserProfileJobs',
  //   async (uid, { rejectWithValue }) => {
  //     try {
  //       const documents = [];
  //       const jobsRef = collection(db, 'jobs');
  //       const q = query(jobsRef, where('uid', '==', uid));
  //       const snap = await getDocs(q);
  //       snap.forEach((doc) => {
  //         documents.push({ ...doc.data() });
  //       });
  //       return documents;
  //     } catch (error) {
  //       return rejectWithValue(error.message);
  //     }
  //   }
  // );

  export const fetchJobsByUserId = createAsyncThunk(
    'jobs/fetchUserProfileJobs',
    async (userId, { rejectWithValue }) => {
      try {
        const jobsSnapshot = await getDocs(
          query(collection(db, 'jobs'), where('uid', '==', userId)) // Check Firestore field name
        );
        const jobs = jobsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        return jobs;
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const fetchViewedSellerJobs = createAsyncThunk(
    'jobs/fetchViewedSellerJobs',
    async (sellerUid, { rejectWithValue }) => {
      try {
        const jobsSnapshot = await getDocs(
          query(collection(db, 'jobs'), where('uid', '==', sellerUid))
        );
        return jobsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  export const fetchJobWithReviews = createAsyncThunk('jobs/fetchJobWithReviews', async (jobId) => {
    try {
      // Reference to the Firestore document for the job
      const jobRef = doc(db, 'jobs', jobId);
      const jobSnapshot = await getDoc(jobRef);

      // Check if the job exists
      if (jobSnapshot.exists()) {
        const job = jobSnapshot.data();
        return job; // Return job data including acceptReview and reviews
      } else {
        throw new Error('Job not found');
      }
    } catch (error) {
      throw new Error(error.message);
    }
  });

  const initialState = {
    cards: [],
    all: [],
    result: [],
    titles: [],
    jobs: [],
    userJobs: [],
    currentUserJobs: [],
    viewedSellerJobs: [],
    lastFetched: null,
    cache: [],
    searchCriteria: {}
  };

  export const jobSlice = createSlice({
    name: 'jobs',
    initialState: initialState,
    reducers: {
      resetJobs() {
        return initialState;
      },
      filterJobs(state, action) {
        let all_jobs = state.all.filter((job) => {
          let statusMatches = job.status === 'active';
          let titleMatches = action.payload.title
            ? job?.title?.toLowerCase() === action.payload?.title?.toLowerCase()
            : true;

          let headlineMatches = action.payload.headline
            ? action.payload.headline
                .toLowerCase()
                .split(' ')
                .some(
                  (word) =>
                    job.headline?.toLowerCase().includes(word) ||
                    job.subcategory?.toLowerCase().includes(word) ||
                    job.description?.toLowerCase().includes(word)
                )
            : true;

          let locationMatches = action.payload.location
            ? job.place === action.payload.location
            : true;

          let categoryMatches = action.payload.category
            ? job.category.toLowerCase() === action.payload.category.toLowerCase()
            : true;

          let priceMatches = true;
          if (action.payload.minPrice !== undefined && action.payload.maxPrice !== undefined) {
            const minPrice = Number(action.payload.minPrice);
            const maxPrice = Number(action.payload.maxPrice);

            if (!isNaN(minPrice) && !isNaN(maxPrice)) {
              priceMatches = job.price >= minPrice && job.price <= maxPrice;
            }
          }

          return (
            statusMatches &&
            titleMatches &&
            headlineMatches &&
            locationMatches &&
            categoryMatches &&
            priceMatches
          );
        });

        state.result = all_jobs;
      },
      clearFilters(state) {
        state.result = state.all;
      },
      setSearchCriteria(state, action) {
        state.searchCriteria = action.payload;
      },
      setAllJobs(state, action) {
        state.all = action.payload;
        state.result = action.payload;
        state.cache = action.payload;
        state.lastFetched = new Date().getTime();
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(createJobForm.fulfilled, (state, action) => {
          const createdJob = { ...action.payload };
          state.cards.push(createdJob);
          if (!state.userJobs.some((job) => job.id === createdJob.id)) {
            state.userJobs.push(createdJob);
          }
          if (!state.currentUserJobs.some((job) => job.id === createdJob.id)) {
            state.currentUserJobs.push(createdJob);
          }
        })
        .addCase(updateJobForm.fulfilled, (state, action) => {
          const updatedJob = { ...action.payload };
          state.cards = mergeJobById(state.cards, updatedJob);
          state.all = mergeJobById(state.all, updatedJob);
          state.result = mergeJobById(state.result, updatedJob);
          state.cache = mergeJobById(state.cache, updatedJob);
          state.userJobs = mergeJobById(state.userJobs, updatedJob);
          state.currentUserJobs = mergeJobById(state.currentUserJobs, updatedJob);
          state.viewedSellerJobs = mergeJobById(state.viewedSellerJobs, updatedJob);
          state.jobs = mergeJobById(state.jobs, updatedJob);
        })
        .addCase(updateScheduleSettings.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(updateScheduleSettings.fulfilled, (state, action) => {
          state.status = 'succeeded';
          const { id, scheduleSettings } = action.payload;
          const job = state.userJobs.find((job) => job.id === id);
          if (job) {
            job.scheduleSettings = scheduleSettings;
          }
          const currentJob = state.currentUserJobs.find((job) => job.id === id);
          if (currentJob) {
            currentJob.scheduleSettings = scheduleSettings;
          }
          const viewedJob = state.viewedSellerJobs.find((job) => job.id === id);
          if (viewedJob) {
            viewedJob.scheduleSettings = scheduleSettings;
          }
        })
        .addCase(updateScheduleSettings.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.error.message;
        })
        .addCase(fetchJobsByQuery.fulfilled, (state, action) => {
          const jobs = [...action.payload];
          const job_list = [];
          jobs.forEach((job) => {
            job_list.push({
              id: job.uid,
              category: [job.category],
              subcategory: [job.subcategory],
              city: [job.place],
              job_title: job.title
            });
          });
          state.cards = action.payload;
          state.titles = [...job_list];
        })
        .addCase(fetchCategoryJobs.fulfilled, (state, action) => {
          state.all = action.payload;
        })
        .addCase(fetchAllJobs.fulfilled, (state, action) => {
          state.all = action.payload;
          state.result = action.payload; //  make it available to dashboard too
          state.cache = action.payload;
          state.lastFetched = new Date().getTime();
        })
        .addCase(fetchActiveJobs.fulfilled, (state, action) => {
          state.all = action.payload;
          state.result = action.payload;
        })

        .addCase(deleteJobById.fulfilled, (state, action) => {
          state.cards = state.cards.filter((c) => c.id !== action.payload);
          state.userJobs = state.userJobs.filter((job) => job.id !== action.payload);
          state.currentUserJobs = state.currentUserJobs.filter((job) => job.id !== action.payload);
          state.viewedSellerJobs = state.viewedSellerJobs.filter((job) => job.id !== action.payload);
        })
        .addCase(toggleJobStatus.fulfilled, (state, action) => {
          const { id, status } = action.payload;

          // Update in cards
          const cardIndex = state.cards.findIndex((c) => c.id === id);
          if (cardIndex !== -1) {
            state.cards[cardIndex].status = status;
          }

          // Update in all
          const allIndex = state.all.findIndex((j) => j.id === id);
          if (allIndex !== -1) {
            state.all[allIndex].status = status;
          }

          // Update in cache
          const cacheIndex = state.cache.findIndex((j) => j.id === id);
          if (cacheIndex !== -1) {
            state.cache[cacheIndex].status = status;
          }

          // Update in userJobs
          const userJobIndex = state.userJobs.findIndex((j) => j.id === id);
          if (userJobIndex !== -1) {
            state.userJobs[userJobIndex].status = status;
          }

          const currentUserJobIndex = state.currentUserJobs.findIndex((j) => j.id === id);
          if (currentUserJobIndex !== -1) {
            state.currentUserJobs[currentUserJobIndex].status = status;
          }

          const viewedSellerJobIndex = state.viewedSellerJobs.findIndex((j) => j.id === id);
          if (viewedSellerJobIndex !== -1) {
            state.viewedSellerJobs[viewedSellerJobIndex].status = status;
          }
        })
        .addCase(fetchAllJobs.rejected, (state, action) => {
          state.error = action.error.message;
        })
        .addCase(fetchJobsByUserId.fulfilled, (state, action) => {
          state.userJobs = action.payload;
          state.currentUserJobs = action.payload;
        })
        .addCase(fetchViewedSellerJobs.pending, (state) => {
          state.viewedSellerJobs = [];
        })
        .addCase(fetchViewedSellerJobs.fulfilled, (state, action) => {
          state.viewedSellerJobs = action.payload;
        })
        .addCase(removeJobFields.fulfilled, (state, action) => {
          const { jobId, fieldsToRemove } = action.payload;
          const removeFields = (job) => {
            fieldsToRemove.forEach((field) => {
              delete job[field];
            });
          };
          [state.jobs, state.userJobs, state.currentUserJobs, state.viewedSellerJobs, state.cards, state.all, state.result, state.cache].forEach((list) => {
            const job = list.find((item) => item.id === jobId);
            if (job) removeFields(job);
          });
        });
    }
  });

  export const { resetJobs, filterJobs, clearFilters, setSearchCriteria, setAllJobs } = jobSlice.actions;

  export const subscribeToAllJobs = (dispatch, onReady) => {
    let firstSnapshot = true;
    const unsubscribe = onSnapshot(collection(db, 'jobs'), (snap) => {
      const jobs = snap.docs.map((d) => {
        const jobData = d.data();
        return {
          ...jobData,
          price: jobData.price ?? 0,
          category: jobData.category || 'Unknown category',
          status: jobData.status || 'active',
          userVatNumber: jobData.userVatNumber || 'N/A'
        };
      });
      dispatch(setAllJobs(jobs));
      if (firstSnapshot) {
        firstSnapshot = false;
        onReady?.();
      }
    });
    return unsubscribe;
  };
  export default jobSlice.reducer;
